import { z } from "zod";
import { validateStep, type CreationLegalForm, type FormAnswers, type StepKey } from "./creation.ts";

/**
 * The questionnaire questions Melrys can invalidate (« Formulaire du demandeur » rows of the back-office), each with
 * the input it is answered with in the creation forms. Shared by the API (validation + write-back of a correction
 * into the dossier's answers), the back-office (the question picked by the agent gives the correction's field) and
 * the client's correction window (same input component, same rules, same value shape as the questionnaire).
 *
 * A question's `key` is the back-office row id (« s3_address », « s6_sec_2 »…); its value is read from / written to
 * the step draft (`step` + `path`), and it is validated with the questionnaire's own `validateStep`, keeping the
 * issues of its own fields only (`issues`).
 */

/** How a question is answered (the creation forms' input of that question). */
export const QuestionKind = z.enum([
  "text", "textarea", "email", "phone", "address", "birth", "date", "closing", "select", "country", "yes_no", "amount", "int",
  "activity", "first_names", "person_name", "trade_name", "domiciliation", "host", "exercise", "nir", "document",
]);
export type QuestionKind = z.infer<typeof QuestionKind>;

/** « Le client corrigera avec : … » (back-office) — what the client will be shown. */
export const QUESTION_KIND_LABELS: Record<QuestionKind, string> = {
  text: "Champ texte",
  textarea: "Texte long",
  email: "Adresse e-mail",
  phone: "Téléphone (indicatif + numéro)",
  address: "Adresse (recherche d'adresse + code postal + ville)",
  birth: "Date et lieu de naissance (jour, mois, année, commune, pays)",
  date: "Date (calendrier)",
  closing: "Jour et mois de clôture",
  select: "Liste de choix",
  country: "Pays (liste)",
  yes_no: "Oui / Non",
  amount: "Montant en euros",
  int: "Nombre entier",
  activity: "Description d'activité (clauses d'objet social proposées)",
  first_names: "Prénoms (jusqu'à 4)",
  person_name: "Prénoms, nom de naissance, nom d'usage",
  trade_name: "Nom commercial (Oui / Non + nom)",
  domiciliation: "Société de domiciliation (SIREN + dénomination)",
  host: "Hébergeur (personne ou société)",
  exercise: "Exercice permanent / saisonnier (+ période)",
  nir: "NIR (nouvelle saisie masquée, 15 chiffres)",
  document: "Document à déposer",
};

/** A scalar inside a structured value (address, birth, phone…). */
const Scalar = z.union([z.string().max(15000), z.number(), z.boolean(), z.null(), z.array(z.string().max(100)).max(4)]);
/** A corrected answer as sent by the client: the questionnaire's own value for that question. */
export const QuestionValue = z.union([z.string().max(15000), z.number(), z.boolean(), z.null(), z.array(z.string().max(100)).max(4), z.record(z.string(), Scalar)]);
export type QuestionValue = z.infer<typeof QuestionValue>;

export interface QuestionChoice {
  value: string;
  label: string;
}

/** What a client needs to render and check the question's input. */
export interface QuestionSpec {
  key: string;
  kind: QuestionKind;
  label: string;
  step: StepKey;
  /** select: the questionnaire's options (stored value + label). */
  choices?: QuestionChoice[];
  /** address: the country is asked too (seat / business address). */
  withCountry?: boolean;
  /** int: unit shown after the number. */
  suffix?: string;
  /** Shown under the input (the questionnaire's hint). */
  hint?: string;
  /** Answer may be left blank (optional question of the form). */
  optional?: boolean;
  /**
   * Write-only secret (AE NIR): never read back nor shown — the client types it again in full, the API keeps it apart
   * from the answers (creation_forms.nir) and every view shows the last 4 digits only.
   */
  secret?: boolean;
}

// ───────────────────────── What can be corrected ─────────────────────────

/**
 * Why a back-office form row cannot be invalidated through a questionnaire correction, and where it is fixed instead:
 *  - `source`  derived value (SASU share count = capital in euros): correct the source question (`sourceKey`);
 *  - `piece`   proven by a piece (identity checked on a validated ID, domiciliation checked on SIRENE): invalidate the
 *              piece (new upload) in « Pièces justificatives »;
 *  - `account` the client's login (e-mail): changed from the client's account settings or by the support.
 * Eligibility answers are « Revue manuelle » (BoFormRow.manualReview), uploaded documents use the piece flow, and
 * system data (references, dates, payments, SIREN / SIRET) are not form rows at all.
 */
export interface NotCorrectable {
  via: "source" | "piece" | "account";
  reason: string;
  sourceKey?: string;
}

/** Rules that hold for every dossier of the form (`lockOf`); the KYC lock depends on the pieces (`KYC_LOCK`). */
export function lockOf(form: CreationLegalForm, key: string): NotCorrectable | null {
  if (form === "SASU" && key === "s2_shares")
    return { via: "source", sourceKey: "s2_capital", reason: "Calculé : le nombre d'actions découle du capital (1 € par action). Corrigez le capital, le nombre d'actions suivra." };
  if ((form !== "AE" && key === "s3_email") || (form === "AE" && key === "s2_email"))
    return { via: "account", reason: "Identifiant de connexion du client : il le modifie depuis son compte (Paramètres), ou le support le change à sa demande." };
  if (form !== "AE" && key === "s5_dom")
    return { via: "piece", reason: "Vérifié au répertoire SIRENE lors de la saisie : en cas d'erreur, invalidez le contrat de domiciliation dans « Pièces justificatives »." };
  return null;
}

/** Identity of the account holder, locked once his ID piece is validated (KYC). */
export const identityKeys = (form: CreationLegalForm) => (form === "AE" ? ["s2_first", "s2_last", "s2_gender", "s2_birth", "s2_nationality"] : ["s3_first", "s3_last", "s3_gender", "s3_birth", "s3_nationality"]);
export const KYC_LOCK: NotCorrectable = {
  via: "piece",
  reason: "Identité vérifiée sur la pièce d'identité validée : pour la corriger, invalidez la pièce d'identité (nouvelle pièce) dans « Pièces justificatives ».",
};

interface QuestionDef extends QuestionSpec {
  /** Dotted path inside the step draft ("" = the draft itself): the value, or the object `fields` are read from. */
  path: string;
  /** Structured value: the sub-fields of the object at `path` (birth, phone, closing…). */
  fields?: string[];
  /** Issue paths of validateStep(step) that belong to this question (prefix match). */
  issues: string[];
  /** Extra fields written next to the value at the step root (e.g. the SASU share count follows the capital). */
  also?: (v: QuestionValue, form: CreationLegalForm) => Record<string, unknown>;
}

/** Marital status agreed with the person's gender: feminine for « Féminin » only, masculine otherwise (unknown, old « Autre »). Never « Marié·e ». */
export const maritalChoices = (gender?: string | null): QuestionChoice[] => {
  const f = gender === "F";
  return [
    { value: "single", label: "Célibataire" },
    { value: "divorced", label: f ? "Divorcée" : "Divorcé" },
    { value: "widowed", label: f ? "Veuve" : "Veuf" },
    { value: "pacs", label: f ? "Pacsée" : "Pacsé" },
    { value: "cohabiting", label: "En concubinage" },
    { value: "married", label: f ? "Mariée" : "Marié" },
  ];
};
export const MARITAL_CHOICES: QuestionChoice[] = maritalChoices(null);
/** G1 (retour 3): no « Autre » — the INPI only knows masculin / féminin. */
const GENDER_CHOICES: QuestionChoice[] = [
  { value: "M", label: "Masculin" },
  { value: "F", label: "Féminin" },
];
const PRO_TYPE_CHOICES: QuestionChoice[] = [
  { value: "domiciliation", label: "Société de domiciliation" },
  { value: "lease", label: "Bail / local professionnel" },
  { value: "third_party", label: "Hébergement par un tiers" },
];
const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

const BIRTH_FIELDS = ["birthDay", "birthMonth", "birthYear", "noBirthDayMonth", "birthCity", "birthCountry", "emancipated"];

/** The identity questions of a person sheet (`base` = path of the PersonDraft in the step). */
function personQuestions(step: StepKey, base: string, keyPrefix: string, who: string, opts: { marital?: boolean; split?: boolean }): QuestionDef[] {
  const at = (f: string) => `${base}.${f}`;
  const w = (l: string) => (who ? `${who} · ${l}` : l);
  const out: QuestionDef[] = [];
  if (opts.split) {
    out.push(
      { key: `${keyPrefix}_first`, kind: "first_names", label: w("Prénom(s)"), step, path: at("firstNames"), issues: [at("firstNames")] },
      { key: `${keyPrefix}_last`, kind: "text", label: w("Nom de naissance"), step, path: at("lastName"), issues: [at("lastName")] },
    );
  } else {
    out.push({ key: `${keyPrefix}_name`, kind: "person_name", label: w("Prénoms et nom"), step, path: base, fields: ["firstNames", "lastName", "usageName"], issues: [at("firstNames"), at("lastName")] });
  }
  out.push(
    { key: `${keyPrefix}_gender`, kind: "select", label: w("Genre"), step, path: at("gender"), choices: GENDER_CHOICES, issues: [at("gender")] },
    { key: `${keyPrefix}_birth`, kind: "birth", label: w("Naissance"), step, path: base, fields: BIRTH_FIELDS, issues: [at("birthDay"), at("birthMonth"), at("birthYear"), at("emancipated"), at("birthCity"), at("birthCountry")] },
    { key: `${keyPrefix}_nationality`, kind: "country", label: w("Nationalité"), step, path: at("nationality"), issues: [at("nationality")] },
  );
  if (opts.marital) out.push({ key: `${keyPrefix}_marital`, kind: "select", label: w("Situation matrimoniale"), step, path: at("maritalStatus"), choices: MARITAL_CHOICES, issues: [at("maritalStatus")] });
  out.push(
    { key: `${keyPrefix}_address`, kind: "address", label: w("Adresse personnelle"), step, path: at("address"), issues: [at("address")] },
    { key: `${keyPrefix}_phone`, kind: "phone", label: w("Téléphone"), step, path: base, fields: ["phonePrefix", "phone"], issues: [at("phone")] },
    { key: `${keyPrefix}_email`, kind: "email", label: w("Adresse e-mail"), step, path: at("email"), issues: [at("email")] },
  );
  return out;
}

function activityQuestions(step: "activites" | "activite", company: boolean, secondaries: number): QuestionDef[] {
  const out: QuestionDef[] = [
    { key: "s6_kind", kind: "exercise", label: "Exercice de l'activité", step, path: "", fields: ["exercise", "seasonStart", "seasonEnd"], issues: ["exercise", "seasonStart", "seasonEnd"] },
  ];
  if (company) out.push({ key: "s6_itinerant", kind: "yes_no", label: "Activité ambulante", step, path: "ambulant", issues: ["ambulant"] });
  out.push({ key: "s6_main", kind: "activity", label: "Description de l'activité principale", step, path: "primary.description", issues: ["primary.description"] });
  for (let i = 0; i < secondaries; i++)
    out.push({ key: `s6_sec_${i + 1}`, kind: "activity", label: `Activité secondaire ${i + 1}`, step, path: `secondaries.${i}.description`, issues: [`secondaries.${i}.description`] });
  return out;
}

const seatQuestions = (step: "siege" | "adresse", company: boolean): QuestionDef[] => [
  { key: "s5_home", kind: "yes_no", label: company ? "Siège au domicile ?" : "Adresse de l'entreprise = domicile ?", step, path: "atHome", issues: ["atHome"] },
  { key: "s5_address", kind: "address", label: company ? "Adresse du siège" : "Adresse de l'entreprise", step, path: "address", withCountry: true, issues: ["address"] },
  ...(company
    ? ([
        { key: "s5_kind", kind: "select", label: "Titre d'occupation", step, path: "proType", choices: PRO_TYPE_CHOICES, issues: ["proType"] },
        { key: "s5_dom", kind: "domiciliation", label: "Société de domiciliation", step, path: "domiciliation", fields: ["siren", "name", "postcode", "city"], issues: ["domiciliation"] },
        { key: "s5_host", kind: "host", label: "Hébergeur", step, path: "thirdParty", fields: ["kind", "firstName", "lastName", "companyName", "siren"], issues: ["thirdParty.kind", "thirdParty.firstName", "thirdParty.lastName", "thirdParty.companyName", "thirdParty.siren"] },
        { key: "s5_host_email", kind: "email", label: "Adresse e-mail de l'hébergeur", step, path: "thirdParty.email", issues: ["thirdParty.email"] },
        { key: "s5_employees", kind: "yes_no", label: "Salariés dans l'établissement ?", step, path: "employeesInEstablishment", issues: ["employeesInEstablishment"] },
        { key: "s5_employees_n", kind: "yes_no", label: "Un ou plusieurs salariés ?", step, path: "hasEmployees", issues: ["hasEmployees"] },
      ] satisfies QuestionDef[])
    : []),
];

function companyQuestions(form: "SASU" | "EURL", a: FormAnswers): QuestionDef[] {
  const sasu = form === "SASU";
  return [
    { key: "s2_name", kind: "text", label: "Nom de l'entreprise", step: "societe", path: "name", issues: ["name"], also: () => ({ nameConfirmed: true }) },
    { key: "s2_trade", kind: "trade_name", label: "Nom commercial", step: "societe", path: "", fields: ["hasTradeName", "tradeName"], issues: ["hasTradeName", "tradeName"] },
    { key: "s2_acronym", kind: "text", label: "Sigle", step: "societe", path: "acronym", issues: ["acronym"], optional: true },
    { key: "s2_duration", kind: "int", label: "Durée de la société", step: "societe", path: "durationYears", suffix: "ans", hint: "Entre 1 et 99 ans.", issues: ["durationYears"] },
    { key: "s2_closing", kind: "closing", label: "Clôture de l'exercice", step: "societe", path: "", fields: ["closingDay", "closingMonth"], issues: ["closingDay", "closingMonth"] },
    { key: "s2_first_closing", kind: "date", label: "Date de première clôture", step: "societe", path: "firstClosingDate", hint: "Entre 6 et 24 mois après la création.", issues: ["firstClosingDate"] },
    {
      key: "s2_capital", kind: "amount", label: "Capital social", step: "societe", path: "capitalCents", issues: ["capitalCents"],
      also: (v) => (sasu ? { sharesCount: typeof v === "number" && v > 0 ? Math.floor(v / 100) : null } : {}),
    },
    ...(sasu ? [] : ([{ key: "s2_shares", kind: "int", label: "Nombre de parts sociales", step: "societe", path: "sharesCount", hint: "Le capital doit se diviser sans reste par ce nombre.", issues: ["sharesCount"] }] satisfies QuestionDef[])),
    { key: "s2_paid", kind: "amount", label: "Montant libéré à la constitution", step: "societe", path: "paidInCents", hint: sasu ? "Au moins 50 % du capital." : "Au moins 20 % du capital.", issues: ["paidInCents"] },
    ...personQuestions("personnes", "associate", "s3", "", { marital: true, split: true }),
    { key: "s3_president", kind: "yes_no", label: sasu ? "Êtes-vous le président ?" : "Êtes-vous le gérant ?", step: "personnes", path: "isLeader", issues: ["isLeader"] },
    ...personQuestions("personnes", "leader", "s3_leader", sasu ? "Président" : "Gérant", {}),
    { key: "s3_dg", kind: "yes_no", label: sasu ? "Directeur général non associé ?" : "Co-gérant ?", step: "personnes", path: "hasExtraLeader", issues: ["hasExtraLeader"] },
    ...personQuestions("personnes", "extraLeader", "s3_extra", sasu ? "DG" : "Co-gérant", {}),
    { key: "s4_tax", kind: "select", label: "Impôt sur les bénéfices", step: "fiscal", path: "incomeTax", choices: [{ value: "IS", label: "IS" }, { value: "IR", label: "IR" }], issues: ["incomeTax"] },
    { key: "s4_vat", kind: "yes_no", label: "Franchise en base de TVA ?", step: "fiscal", path: "vatFranchise", issues: ["vatFranchise"] },
    ...seatQuestions("siege", true),
    ...activityQuestions("activites", true, a.activites?.secondaries?.length ?? 0),
  ];
}

function aeQuestions(a: FormAnswers): QuestionDef[] {
  const person = personQuestions("identite", "declarant", "s2", "", { marital: true, split: true });
  // The back-office shows marital status and address under « Situation » (s3_*), the rest under « Identité » (s2_*).
  const moved = (k: string, to: string) => person.filter((q) => q.key === k).map((q) => ({ ...q, key: to }));
  return [
    ...person.filter((q) => !["s2_marital", "s2_address"].includes(q.key)),
    ...moved("s2_marital", "s3_marital"),
    ...moved("s2_address", "s3_address"),
    { key: "s2_nir", kind: "nir", label: "Numéro de sécurité sociale (NIR)", step: "identite", path: "nir", secret: true, hint: "15 chiffres, sur votre carte Vitale. Il n'est jamais réaffiché en entier.", issues: ["nir"] },
    { key: "s2_trade", kind: "trade_name", label: "Nom commercial", step: "identite", path: "", fields: ["hasTradeName", "tradeName"], issues: ["hasTradeName", "tradeName"] },
    { key: "s4_vl", kind: "yes_no", label: "Versement libératoire ?", step: "cotisations", path: "liberatoire", issues: ["liberatoire"] },
    { key: "s4_acre", kind: "yes_no", label: "ACRE ?", step: "cotisations", path: "acre", issues: ["acre"] },
    { key: "s4_period", kind: "select", label: "Déclaration URSSAF", step: "cotisations", path: "frequency", choices: [{ value: "monthly", label: "Mensuelle" }, { value: "quarterly", label: "Trimestrielle" }], issues: ["frequency"] },
    ...seatQuestions("adresse", false),
    ...activityQuestions("activite", false, a.activite?.secondaries?.length ?? 0),
  ];
}

function defsFor(form: CreationLegalForm, answers: FormAnswers): QuestionDef[] {
  return (form === "AE" ? aeQuestions(answers) : companyQuestions(form, answers)).filter((q) => !lockOf(form, q.key));
}

const specOf = ({ key, kind, label, step, choices, withCountry, suffix, hint, optional, secret }: QuestionDef): QuestionSpec => ({
  key, kind, label, step, ...(choices ? { choices } : {}), ...(withCountry ? { withCountry } : {}), ...(suffix ? { suffix } : {}), ...(hint ? { hint } : {}), ...(optional ? { optional } : {}), ...(secret ? { secret } : {}),
});

/** Every question of a dossier's questionnaire (secondary activities as many as answered). */
export function questionsFor(form: CreationLegalForm, answers: FormAnswers = {}): QuestionSpec[] {
  return defsFor(form, answers).map(specOf);
}

/**
 * The question behind a back-office row / correction item. Secondary activities exist up to the number of
 * activities answered (a key beyond that still resolves, so an old item stays answerable).
 */
export function questionFor(form: CreationLegalForm, key: string | null | undefined, answers: FormAnswers = {}): QuestionSpec | null {
  const d = defFor(form, key, answers);
  return d ? specOf(d) : null;
}

function defFor(form: CreationLegalForm, key: string | null | undefined, answers: FormAnswers): QuestionDef | null {
  if (!key) return null;
  const sec = /^s6_sec_(\d+)$/.exec(key);
  const n = sec ? Math.max(Number(sec[1]), 0) : 0;
  const a = sec ? { ...answers, activites: { ...answers.activites, secondaries: Array.from({ length: Math.max(n, answers.activites?.secondaries?.length ?? 0) }, (_, i) => answers.activites?.secondaries?.[i] ?? { id: `s${i}` }) }, activite: { ...answers.activite, secondaries: Array.from({ length: Math.max(n, answers.activite?.secondaries?.length ?? 0) }, (_, i) => answers.activite?.secondaries?.[i] ?? { id: `s${i}` }) } } : answers;
  const d = defsFor(form, a).find((q) => q.key === key) ?? null;
  // Marital choices agree with the gender of the same person sheet (« Mariée » / « Marié »).
  if (d?.path.endsWith("maritalStatus")) return { ...d, choices: maritalChoices(getIn(answers[d.step], d.path.replace(/maritalStatus$/, "gender")) as string | null) };
  return d;
}

// ───────────────────────── Read / write ─────────────────────────

type Obj = Record<string, unknown>;
const isObj = (x: unknown): x is Obj => !!x && typeof x === "object" && !Array.isArray(x);

function getIn(obj: unknown, path: string): unknown {
  if (!path) return obj;
  return path.split(".").reduce<unknown>((o, k) => (isObj(o) ? o[k] : Array.isArray(o) ? o[Number(k)] : undefined), obj);
}

function setIn(obj: unknown, path: string, value: unknown): unknown {
  if (!path) return value;
  const [k = "", ...rest] = path.split(".");
  const tail = rest.join(".");
  if (/^\d+$/.test(k)) {
    const arr: unknown[] = Array.isArray(obj) ? [...(obj as unknown[])] : [];
    arr[Number(k)] = setIn(arr[Number(k)], tail, value);
    return arr;
  }
  const base: Obj = isObj(obj) ? { ...obj } : {};
  base[k] = setIn(base[k], tail, value);
  return base;
}

/** The question's current answer, in its value shape (structured kinds: an object of their fields). */
export function readQuestion(form: CreationLegalForm, answers: FormAnswers, key: string): QuestionValue {
  const d = defFor(form, key, answers);
  if (!d || d.secret) return null;
  const at = getIn(answers[d.step], d.path);
  if (d.fields) {
    const o = isObj(at) ? at : {};
    return Object.fromEntries(d.fields.map((f) => [f, (o[f] ?? null) as z.infer<typeof Scalar>]));
  }
  if (d.kind === "address") return isObj(at) ? (at as Record<string, string>) : {};
  return (at ?? null) as QuestionValue;
}

/** The answers with the question's value replaced (a new object; the input is not modified). */
export function applyQuestion(form: CreationLegalForm, answers: FormAnswers, key: string, value: QuestionValue): FormAnswers {
  const d = defFor(form, key, answers);
  if (!d) return answers;
  let step = (answers[d.step] ?? {}) as Obj;
  if (d.fields) {
    const prev = getIn(step, d.path);
    const merged: Obj = { ...(isObj(prev) ? prev : {}), ...(isObj(value) ? Object.fromEntries(Object.entries(value).filter(([k]) => d.fields!.includes(k))) : {}) };
    // An empty sub-field is « not answered »: left out, as the questionnaire's drafts do (most are not nullable).
    for (const f of d.fields) if (merged[f] === null || merged[f] === undefined) delete merged[f];
    step = setIn(step, d.path, merged) as Obj;
  } else if (d.kind === "address") {
    const merged: Obj = isObj(value) ? { ...value } : {};
    for (const f of Object.keys(merged)) if (merged[f] === null || merged[f] === undefined) delete merged[f];
    step = setIn(step, d.path, merged) as Obj;
  } else {
    // Empty = not answered: the key is left out (string drafts are not nullable).
    const v = value === "" && (d.kind === "date" || d.kind === "select") ? null : value;
    step = setIn(step, d.path, v === null ? undefined : v) as Obj;
    if (v === null) dropIn(step, d.path);
  }
  if (d.also) step = { ...step, ...d.also(value, form) };
  return { ...answers, [d.step]: step };
}

function dropIn(obj: Obj, path: string) {
  const parts = path.split(".");
  const last = parts.pop()!;
  const parent = getIn(obj, parts.join("."));
  if (isObj(parent)) delete parent[last];
}

// ───────────────────────── Validation ─────────────────────────

const str = z.string().max(15000);
const opt = <T extends z.ZodType>(s: T) => s.nullable().optional();

/** Shape of each kind's value (the questionnaire's draft types). */
function shapeOf(d: QuestionDef): z.ZodType {
  switch (d.kind) {
    case "text":
    case "textarea":
    case "email":
    case "activity":
    case "country":
      return str;
    case "nir":
      return z.string().max(30);
    case "date":
      return z.union([z.iso.date(), z.literal("")]);
    case "select":
      return z.enum((d.choices ?? []).map((c) => c.value) as [string, ...string[]]);
    case "yes_no":
      return z.boolean();
    case "amount":
    case "int":
      return z.number().int().min(0).max(999_999_999_99);
    case "first_names":
      return z.array(z.string().max(100)).min(1).max(4);
    case "address":
      return z.object({ street: opt(str), complement: opt(str), postcode: opt(str), city: opt(str), country: opt(str) });
    case "phone":
      return z.object({ phonePrefix: opt(str), phone: opt(str) });
    case "birth":
      return z.object({
        birthDay: opt(z.number().int().min(1).max(31)), birthMonth: opt(z.number().int().min(1).max(12)), birthYear: opt(z.number().int().min(1900).max(2100)),
        noBirthDayMonth: opt(z.boolean()), birthCity: opt(str), birthCountry: opt(str), emancipated: opt(z.boolean()),
      });
    case "closing":
      return z.object({ closingDay: opt(z.number().int().min(1).max(31)), closingMonth: opt(z.number().int().min(1).max(12)) });
    case "person_name":
      return z.object({ firstNames: opt(z.array(z.string().max(100)).max(4)), lastName: opt(str), usageName: opt(str) });
    case "trade_name":
      return z.object({ hasTradeName: opt(z.boolean()), tradeName: opt(z.string().max(200)) });
    case "domiciliation":
      return z.object({ siren: opt(str), name: opt(str), postcode: opt(str), city: opt(str) });
    case "host":
      return z.object({ kind: opt(z.enum(["person", "company"])), firstName: opt(str), lastName: opt(str), companyName: opt(str), siren: opt(str) });
    case "exercise":
      return z.object({ exercise: opt(z.enum(["permanent", "seasonal"])), seasonStart: opt(z.union([z.iso.date(), z.literal("")])), seasonEnd: opt(z.union([z.iso.date(), z.literal("")])) });
    case "document":
      return z.null();
  }
}

export interface QuestionIssue {
  /** Dotted path inside the step draft (the questionnaire's issue path). */
  path: string;
  message: string;
}

const REQUIRED = "Ce champ est requis.";
const blank = (v: unknown) => v == null || (typeof v === "string" && !v.trim()) || (Array.isArray(v) && !v.some((x) => typeof x === "string" && x.trim()));

/**
 * Checks a corrected value with the questionnaire's rules: shape of the question's value, then validateStep on the
 * answers with the value applied, keeping the issues of this question's fields (plus « requis » for a question the
 * form always asks). `today`: the dossier clock (dates « dans le futur », first closing window…).
 */
export function validateQuestion(form: CreationLegalForm, answers: FormAnswers, key: string, value: unknown, today: Date): QuestionIssue[] {
  const d = defFor(form, key, answers);
  if (!d) return [{ path: "", message: "Cette question n'existe pas dans votre formulaire." }];
  const parsed = shapeOf(d).safeParse(value);
  if (!parsed.success) return [{ path: d.path, message: d.kind === "select" ? "Choisissez une réponse dans la liste." : d.kind === "date" ? "Date invalide." : "Réponse invalide." }];
  const v = parsed.data as QuestionValue;
  if (!d.optional && !d.fields && d.kind !== "address" && blank(v)) return [{ path: d.path, message: d.kind === "yes_no" || d.kind === "select" ? "Choisissez une réponse." : REQUIRED }];
  const next = applyQuestion(form, answers, key, v);
  const mine = (p: string) => d.issues.some((x) => p === x || p.startsWith(`${x}.`));
  return validateStep(d.step, next, { form, today, nirSaved: true })
    .filter((i) => mine(i.path))
    .map((i) => ({ path: i.path, message: i.message }));
}

// ───────────────────────── Display ─────────────────────────

const s = (v: unknown) => (typeof v === "string" ? v.trim() : "");
const n = (v: unknown) => (typeof v === "number" ? v : null);
const euros = (cents: number) => `${new Intl.NumberFormat("fr-FR").format(Math.round(cents / 100)).replace(/[  ]/g, " ")} €`;
const frDate = (iso: string) => iso.slice(0, 10).split("-").reverse().join("/");

/** One line for a question's value (« Votre réponse actuelle », history, back-office review). */
export function displayQuestion(spec: Pick<QuestionSpec, "kind" | "choices" | "suffix">, value: unknown): string {
  const o = isObj(value) ? value : {};
  switch (spec.kind) {
    case "yes_no":
      return value === true ? "Oui" : value === false ? "Non" : "—";
    case "select":
      return spec.choices?.find((c) => c.value === value)?.label ?? (s(value) || "—");
    case "amount":
      return n(value) == null ? "—" : euros(n(value)!);
    case "int":
      return n(value) == null ? "—" : `${n(value)}${spec.suffix ? ` ${spec.suffix}` : ""}`;
    case "date":
      return s(value) ? frDate(s(value)) : "—";
    case "first_names":
      return Array.isArray(value) ? value.filter((x) => typeof x === "string" && x.trim()).join(" ") || "—" : s(value) || "—";
    case "address": {
      const city = [s(o.postcode), s(o.city)].filter(Boolean).join(" ");
      return [s(o.street), s(o.complement), city, s(o.country) && s(o.country) !== "France" ? s(o.country) : ""].filter(Boolean).join(" · ") || "—";
    }
    case "phone":
      return s(o.phone) ? `${s(o.phonePrefix) || "+33"} ${s(o.phone).replace(/\s/g, "").replace(/^0(?=\d{9}$)/, "")}` : "—";
    case "birth": {
      const y = n(o.birthYear);
      if (!y) return "—";
      const d = n(o.birthDay);
      const m = n(o.birthMonth);
      const day = o.noBirthDayMonth || !d || !m ? `${y}` : `${d === 1 ? "1er" : d} ${MONTHS[m - 1]} ${y}`;
      return [day, s(o.birthCity), s(o.birthCountry) || "France"].filter(Boolean).join(" · ");
    }
    case "closing":
      return n(o.closingDay) && n(o.closingMonth) ? `${n(o.closingDay)} ${MONTHS[n(o.closingMonth)! - 1]}` : "—";
    case "person_name": {
      const first = Array.isArray(o.firstNames) ? o.firstNames.filter((x) => typeof x === "string" && x.trim()).join(" ") : "";
      return [first, s(o.lastName), s(o.usageName) ? `(nom d'usage ${s(o.usageName)})` : ""].filter(Boolean).join(" ") || "—";
    }
    case "trade_name":
      return o.hasTradeName === true ? `Oui · ${s(o.tradeName) || "—"}` : o.hasTradeName === false ? "Non" : "—";
    case "domiciliation":
      return [s(o.name), s(o.siren) ? `SIREN ${s(o.siren)}` : "", [s(o.postcode), s(o.city)].filter(Boolean).join(" ")].filter(Boolean).join(" · ") || "—";
    case "host":
      return (o.kind === "company" ? [s(o.companyName), s(o.siren) ? `SIREN ${s(o.siren)}` : ""] : [s(o.firstName), s(o.lastName)]).filter(Boolean).join(" ") || "—";
    case "exercise":
      return o.exercise === "seasonal" ? `Saisonnière${s(o.seasonStart) && s(o.seasonEnd) ? ` · du ${frDate(s(o.seasonStart))} au ${frDate(s(o.seasonEnd))}` : ""}` : o.exercise === "permanent" ? "Permanente" : "—";
    case "nir": {
      const d = typeof value === "string" ? value.replace(/\D/g, "") : "";
      return d ? `••• ${d.slice(-4)}` : "—";
    }
    case "document":
      return "—";
    default:
      return s(value) || "—";
  }
}
